import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  ArrowRight,
  Brain,
  Calendar,
  Clock,
  Flame,
  Loader2,
  Radar,
  RefreshCw,
  Repeat,
  Sparkles,
  TrendingDown,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { API, formatNumber, getPlatformName } from "../constants";

const COPY = {
  ko: {
    title: "탐지된 패턴",
    description: "스필오버 데이터에서 ML 기반으로 탐지된 반복 패턴과 인사이트입니다.",
    refresh: "다시 분석",
    refreshing: "분석 중...",
    cached: "캐시",
    live: "최신",
    noPatterns: "현재 데이터에서 유의미한 패턴이 탐지되지 않았습니다.",
    noPatternsDesc: "더 긴 분석 기간(60~90일)을 사용하거나 이벤트 데이터가 축적되면 패턴이 탐지됩니다.",
    events: "이벤트",
    pairs: "페어",
    period: "분석 기간",
    days: "일",
    narrativeTitle: "AI 인사이트 해석",
    recurring_flow: "반복 흐름",
    timing: "타이밍 패턴",
    viral_threshold: "바이럴 임계점",
    decay: "효과 감쇠",
    compound: "복합 효과",
    high: "높음",
    medium: "보통",
    low: "낮음",
    recurringDesc: "동일 source→target 쌍이 반복적으로 관찰됩니다",
    timingDesc: "게시 시점에 따라 스필오버 효과가 다릅니다",
    thresholdDesc: "특정 바이럴 점수 이상에서 효과가 급증합니다",
    decayDesc: "게시 후 시간 경과에 따른 효과 감쇠 패턴입니다",
    compoundDesc: "복수 바이럴 동시 발생 시 효과가 증폭됩니다",
    errorToast: "패턴 분석에 실패했습니다",
    avgLag: "평균 래그",
    posRatio: "긍정 비율",
    totalLift: "총 Lift",
    stronger: "더 강한 시점",
    weekday: "평일",
    weekend: "주말",
    bestRange: "최적 구간",
    amplification: "증폭 배수",
    peakDay: "피크 일차",
    totalDecay: "총 감쇠",
    soloAvg: "단독 평균",
    compoundAvg: "복합 평균",
    amplified: "증폭",
    dampened: "감쇠",
  },
  en: {
    title: "Detected Patterns",
    description: "ML-detected recurring patterns and insights from spillover data.",
    refresh: "Re-analyze",
    refreshing: "Analyzing...",
    cached: "Cached",
    live: "Fresh",
    noPatterns: "No significant patterns detected in current data.",
    noPatternsDesc: "Try a longer analysis period (60-90 days) or wait for more event data.",
    events: "events",
    pairs: "pairs",
    period: "Analysis period",
    days: "d",
    narrativeTitle: "AI Insight Narrative",
    recurring_flow: "Recurring Flow",
    timing: "Timing Pattern",
    viral_threshold: "Viral Threshold",
    decay: "Effect Decay",
    compound: "Compound Effect",
    high: "High",
    medium: "Medium",
    low: "Low",
    recurringDesc: "Same source→target pair observed repeatedly",
    timingDesc: "Spillover effect varies by posting time",
    thresholdDesc: "Effects spike above a specific viral score",
    decayDesc: "Effect diminishes over days after posting",
    compoundDesc: "Multiple concurrent viral events amplify effect",
    errorToast: "Pattern analysis failed",
    avgLag: "Avg Lag",
    posRatio: "Positive %",
    totalLift: "Total Lift",
    stronger: "Stronger",
    weekday: "Weekday",
    weekend: "Weekend",
    bestRange: "Best Range",
    amplification: "Amplification",
    peakDay: "Peak Day",
    totalDecay: "Total Decay",
    soloAvg: "Solo Avg",
    compoundAvg: "Compound Avg",
    amplified: "Amplified",
    dampened: "Dampened",
  },
  zh: {
    title: "检测到的模式",
    description: "基于 ML 从溢出数据中检测到的重复模式和洞察。",
    refresh: "重新分析",
    refreshing: "分析中...",
    cached: "缓存",
    live: "最新",
    noPatterns: "当前数据未检测到显著模式。",
    noPatternsDesc: "尝试使用更长的分析期间（60-90天）或等待更多事件数据。",
    events: "事件",
    pairs: "配对",
    period: "分析期间",
    days: "天",
    narrativeTitle: "AI 洞察解读",
    recurring_flow: "重复流向",
    timing: "时间模式",
    viral_threshold: "热门阈值",
    decay: "效果衰减",
    compound: "复合效果",
    high: "高",
    medium: "中",
    low: "低",
    recurringDesc: "相同来源→目标配对被反复观察到",
    timingDesc: "溢出效果因发布时间而异",
    thresholdDesc: "效果在特定热门分数以上急剧增加",
    decayDesc: "发布后效果随时间衰减的模式",
    compoundDesc: "多个热门事件同时发生时效果被放大",
    errorToast: "模式分析失败",
    avgLag: "平均滞后",
    posRatio: "正面比例",
    totalLift: "总 Lift",
    stronger: "更强时段",
    weekday: "工作日",
    weekend: "周末",
    bestRange: "最佳区间",
    amplification: "放大倍数",
    peakDay: "峰值日",
    totalDecay: "总衰减",
    soloAvg: "单独平均",
    compoundAvg: "复合平均",
    amplified: "放大",
    dampened: "抑制",
  },
};

const PATTERN_ICONS = {
  recurring_flow: Repeat,
  timing: Clock,
  viral_threshold: Flame,
  decay: TrendingDown,
  compound: Zap,
};

const CONFIDENCE_COLORS = {
  high: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600",
  medium: "border-amber-500/30 bg-amber-500/10 text-amber-600",
  low: "border-zinc-500/30 bg-zinc-500/10 text-zinc-500",
};

const parseMarkdownBlocks = (markdown) => {
  const blocks = [];
  let listItems = [];
  let ordered = false;

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push({ type: ordered ? "ol" : "ul", items: [...listItems] });
    listItems = [];
  };

  (markdown || "").split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) { flushList(); blocks.push({ type: "space" }); return; }
    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) { if (listItems.length && !ordered) flushList(); ordered = true; listItems.push(orderedMatch[1]); return; }
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) { if (listItems.length && ordered) flushList(); ordered = false; listItems.push(trimmed.slice(2)); return; }
    flushList(); ordered = false;
    if (trimmed.startsWith("### ")) { blocks.push({ type: "h3", text: trimmed.slice(4) }); return; }
    if (trimmed.startsWith("## ")) { blocks.push({ type: "h2", text: trimmed.slice(3) }); return; }
    if (trimmed.startsWith("# ")) { blocks.push({ type: "h1", text: trimmed.slice(2) }); return; }
    blocks.push({ type: "p", text: trimmed });
  });
  flushList();
  return blocks;
};

const MarkdownContent = ({ markdown }) => {
  const blocks = useMemo(() => parseMarkdownBlocks(markdown), [markdown]);
  const renderInline = (text) => {
    if (!text) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => part.startsWith("**") && part.endsWith("**") ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong> : part);
  };
  return (
    <div className="space-y-2.5">
      {blocks.map((block, i) => {
        if (block.type === "space") return <div key={`s-${i}`} className="h-1" />;
        if (block.type === "h1") return <h2 key={`h1-${i}`} className="text-xl font-semibold tracking-tight text-foreground">{renderInline(block.text)}</h2>;
        if (block.type === "h2") return <h3 key={`h2-${i}`} className="pt-1.5 text-base font-semibold text-foreground">{renderInline(block.text)}</h3>;
        if (block.type === "h3") return <h4 key={`h3-${i}`} className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground">{renderInline(block.text)}</h4>;
        if (block.type === "ol") return <ol key={`ol-${i}`} className="space-y-1.5 pl-5 text-sm leading-7 text-muted-foreground list-decimal">{block.items.map((item, j) => <li key={`ol-${j}`}>{renderInline(item)}</li>)}</ol>;
        if (block.type === "ul") return <ul key={`ul-${i}`} className="space-y-1.5 pl-5 text-sm leading-7 text-muted-foreground list-disc">{block.items.map((item, j) => <li key={`ul-${j}`}>{renderInline(item)}</li>)}</ul>;
        return <p key={`p-${i}`} className="text-sm leading-7 text-muted-foreground">{renderInline(block.text)}</p>;
      })}
    </div>
  );
};

const PatternDetail = ({ pattern, lang, copy }) => {
  const d = pattern.data;
  switch (pattern.type) {
    case "recurring_flow":
      return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span><Badge variant="outline" className="text-[10px]">{getPlatformName(d.source_platform, lang)}</Badge> <ArrowRight className="inline h-3 w-3" /> <Badge variant="outline" className="text-[10px]">{getPlatformName(d.target_platform, lang)}</Badge></span>
          <span>{copy.events} {d.event_count}</span>
          <span>{copy.posRatio} {d.positive_ratio}%</span>
          <span>{copy.avgLag} {d.avg_lag_days}{copy.days}</span>
          <span>{copy.totalLift} {formatNumber(d.total_lift)}</span>
        </div>
      );
    case "timing":
      return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>{copy.stronger}: <strong className="text-foreground">{d.stronger_period === "weekend" ? copy.weekend : copy.weekday}</strong></span>
          <span>{copy.weekday}: {formatNumber(d.weekday_avg_delta)} ({d.weekday_count})</span>
          <span>{copy.weekend}: {formatNumber(d.weekend_avg_delta)} ({d.weekend_count})</span>
          <span>{d.difference_pct > 0 ? "+" : ""}{d.difference_pct}%</span>
        </div>
      );
    case "viral_threshold":
      return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>{copy.bestRange}: <strong className="text-foreground">{d.best_range}</strong></span>
          <span>{copy.amplification}: {d.amplification_factor}x</span>
          {Object.entries(d.bucket_averages || {}).map(([k, v]) => (
            <span key={k}>score {k}: {formatNumber(v)} ({d.bucket_counts?.[k] || 0})</span>
          ))}
        </div>
      );
    case "decay":
      return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>{copy.peakDay}: Day {d.peak_day} ({formatNumber(d.peak_avg_delta)})</span>
          <span>{copy.totalDecay}: {d.total_decay_pct}%</span>
          <span>{d.days_analyzed} {lang === "ko" ? "일 분석" : lang === "zh" ? "天分析" : "days"}</span>
        </div>
      );
    case "compound":
      return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>{copy.soloAvg}: {formatNumber(d.solo_avg_delta)} ({d.solo_count})</span>
          <span>{copy.compoundAvg}: {formatNumber(d.compound_avg_delta)} ({d.compound_count})</span>
          <span>{copy.amplification}: {d.amplification_factor}x ({d.direction === "amplified" ? copy.amplified : copy.dampened})</span>
        </div>
      );
    default:
      return null;
  }
};

export const SpilloverPatternInsights = ({ lang = "ko", headers, selectedGroup, days = 90 }) => {
  const copy = COPY[lang] || COPY.ko;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchPatterns = useCallback(async (forceRefresh = false) => {
    if (!selectedGroup) return;
    if (!forceRefresh) setData(null);
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/spillover/patterns`, {
        headers,
        params: { group_id: selectedGroup, days: Math.max(days, 60), language: lang, force_refresh: forceRefresh },
      });
      setData(response.data);
    } catch {
      setError(copy.errorToast);
      if (forceRefresh) toast.error(copy.errorToast);
    } finally {
      setLoading(false);
    }
  }, [copy.errorToast, days, headers, lang, selectedGroup]);

  useEffect(() => {
    if (selectedGroup) fetchPatterns(false);
  }, [fetchPatterns, selectedGroup]);

  return (
    <Card data-testid="spillover-pattern-insights-card" className="border-primary/20 bg-gradient-to-b from-primary/4 via-background to-background">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary" data-testid="pattern-ml-badge">
                <Brain className="mr-1 h-3.5 w-3.5" />
                ML
              </Badge>
              {data ? (
                <Badge variant="outline" className="border-border/60 bg-background/70 text-foreground" data-testid="pattern-cache-badge">
                  {data.cached ? copy.cached : copy.live}
                </Badge>
              ) : null}
            </div>
            <CardTitle className="text-sm font-semibold">{copy.title}</CardTitle>
            <CardDescription className="max-w-2xl">{copy.description}</CardDescription>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fetchPatterns(true)}
            disabled={!selectedGroup || loading}
            data-testid="pattern-refresh-button"
          >
            {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            {loading ? copy.refreshing : copy.refresh}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!selectedGroup ? (
          <div className="rounded-3xl border border-dashed border-border/60 bg-background/60 p-8 text-center text-sm text-muted-foreground" data-testid="pattern-empty">
            {lang === "ko" ? "그룹을 선택하면 패턴이 자동으로 분석됩니다." : lang === "zh" ? "选择分组后将自动分析模式。" : "Select a group to analyze patterns."}
          </div>
        ) : null}

        {selectedGroup && loading && !data ? (
          <div className="space-y-3" data-testid="pattern-loading">
            <div className="rounded-3xl border border-border/60 bg-background/70 p-5">
              <div className="flex items-start gap-3">
                <div className="rounded-full border border-primary/20 bg-primary/10 p-2"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{lang === "ko" ? "패턴을 분석하고 있습니다" : lang === "zh" ? "正在分析模式" : "Analyzing patterns"}</p>
                  <p className="text-sm text-muted-foreground">{lang === "ko" ? "스필오버 데이터에서 반복 패턴과 인사이트를 탐지 중입니다." : lang === "zh" ? "正在从溢出数据中检测重复模式和洞察。" : "Detecting recurring patterns and insights from spillover data."}</p>
                </div>
              </div>
            </div>
            <Skeleton className="h-60 w-full rounded-3xl" />
          </div>
        ) : null}

        {selectedGroup && error && !data ? (
          <div className="rounded-3xl border border-rose-500/20 bg-rose-500/5 p-5" data-testid="pattern-error">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-rose-500 shrink-0" />
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button type="button" size="sm" variant="outline" onClick={() => fetchPatterns(true)}><RefreshCw className="mr-1 h-3.5 w-3.5" />{copy.refresh}</Button>
              </div>
            </div>
          </div>
        ) : null}

        {data && !data.patterns?.length ? (
          <div className="rounded-3xl border border-dashed border-border/60 bg-background/60 p-8 text-center" data-testid="pattern-no-patterns">
            <Radar className="mx-auto mb-3 h-8 w-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">{copy.noPatterns}</p>
            <p className="mt-1 text-xs text-muted-foreground/60">{copy.noPatternsDesc}</p>
          </div>
        ) : null}

        {data?.patterns?.length ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="pattern-meta">
              <span>{data.total_events_analyzed} {copy.events}</span>
              <span>·</span>
              <span>{data.total_pairs_analyzed} {copy.pairs}</span>
              <span>·</span>
              <span>{copy.period} {data.analysis_period_days}{copy.days}</span>
            </div>

            <div className="space-y-2" data-testid="pattern-list">
              {data.patterns.map((pattern, index) => {
                const Icon = PATTERN_ICONS[pattern.type] || Sparkles;
                const typeLabel = copy[pattern.type] || pattern.type;
                const confColor = CONFIDENCE_COLORS[pattern.confidence] || CONFIDENCE_COLORS.low;
                const confLabel = copy[pattern.confidence] || pattern.confidence;
                return (
                  <div
                    key={`${pattern.type}-${index}`}
                    className="rounded-2xl border border-border/60 bg-background/70 p-3 space-y-2"
                    data-testid={`pattern-card-${index}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="rounded-lg border border-border/60 bg-muted/30 p-1.5">
                          <Icon className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span className="text-sm font-medium text-foreground">{typeLabel}</span>
                        <Badge variant="outline" className={`text-[10px] ${confColor}`}>{confLabel}</Badge>
                      </div>
                      <span className="text-xs font-mono text-muted-foreground" data-testid={`pattern-impact-${index}`}>
                        impact {pattern.impact_score}
                      </span>
                    </div>
                    <PatternDetail pattern={pattern} lang={lang} copy={copy} />
                  </div>
                );
              })}
            </div>

            {data.narrative_markdown ? (
              <div className="rounded-[28px] border border-border/60 bg-background/80 p-5 md:p-6 space-y-3" data-testid="pattern-narrative">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  <p className="text-sm font-semibold text-foreground">{copy.narrativeTitle}</p>
                </div>
                <MarkdownContent markdown={data.narrative_markdown} />
              </div>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};
